// Layer-2 org-branding CSS overlay (story 6-8, P10 §1/§3). P10 §3's literal
// boot sequence: "Boot → GET /v1/me/capabilities (+ org branding...) → inject
// Layer-2 CSS vars → CapabilityProvider... render app" — `CapabilityProvider`
// (lib/capabilities.tsx) calls `applyBrandingOverlay` whenever its fetched
// `doc.branding` changes; `main.tsx` calls it SYNCHRONOUSLY, before React's
// first render, with whatever was cached from the LAST successful boot, so a
// returning user's second-and-later visits never paint the default token
// before the branded one (the realistic "no unbranded flash" bar — no
// client-only trick can eliminate the fetch latency on someone's literal
// first-ever page load; that would need server-side rendering, which this SPA
// does not have).
//
// Colors travel the wire as hex (`OrgBrandingOverlay.primaryColor`/
// `primaryForeground`, both server-derived/validated by
// `orgBranding.service#setBranding`'s save-time WCAG AA gate) but
// `tokens.css`'s `--primary` etc. are bare `H S% L%` triples
// (tailwind.config.ts's `hsl(var(--x) / <alpha-value>)` wiring requires
// exactly that shape — tokens.css's own "Tailwind wiring constraint"
// comment). This file owns its own small hex→HSL conversion rather than
// importing `modules/reports/reportColor.ts`'s (scoped to a different
// surface — REPORT render-time text-color derivation) — the same
// "duplicate on purpose, one twin per real surface" precedent that file's
// own header already argues for, applied here to a third surface (boot-time
// CSS var injection, not a render-time derivation).
const STORAGE_KEY = 'sitewise.branding.v1';

// Story 6-9 — the curated, allow-listed brand-font set (see
// `contracts/orgBranding.js`'s header for the full "why these three, why no
// upload/CDN" argument: `'outfit'` reuses the SAME `@fontsource/outfit`
// files `main.tsx` already self-hosts for `--font-display`, applied to BOTH
// roles; `'system'`/`'serif'` are pure OS-native stacks, zero bytes loaded).
export type BrandFont = 'outfit' | 'system' | 'serif';

export const BRAND_FONT_STACKS: Record<BrandFont, string> = {
  outfit: "'Outfit', 'Plus Jakarta Sans', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  system: "system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
  serif: "Georgia, 'Times New Roman', Times, serif",
};

export interface BrandingOverlay {
  logoUrl: string | null;
  primaryColor: string | null;
  primaryForeground: string | null;
  // Story 6-9 — the "portal accent" role (P10 §1; see contracts/orgBranding.js's
  // header for the full --accent mapping argument).
  accentColor: string | null;
  accentForeground: string | null;
  // Story 6-9 — never client-supplied (server-derived from the upload the
  // same way logoUrl is).
  faviconUrl: string | null;
  font: BrandFont | null;
}

export const EMPTY_BRANDING_OVERLAY: BrandingOverlay = {
  logoUrl: null,
  primaryColor: null,
  primaryForeground: null,
  accentColor: null,
  accentForeground: null,
  faviconUrl: null,
  font: null,
};

const VALID_FONTS: BrandFont[] = ['outfit', 'system', 'serif'];
const isBrandFont = (value: unknown): value is BrandFont => typeof value === 'string' && (VALID_FONTS as string[]).includes(value);

// The default favicon `index.html` ships (`#app-favicon`'s own `href` at
// parse time) — read ONCE at module-eval time, before any override can have
// run, so a cleared/absent tenant favicon always has a real default to
// restore rather than leaving a stale foreign icon in the tab.
const DEFAULT_FAVICON_HREF = document.getElementById('app-favicon')?.getAttribute('href') ?? '/favicon.svg';

function hexToHslTriple(hex: string): string | null {
  const cleaned = hex.replace('#', '').trim();
  if (!/^[0-9a-fA-F]{6}$/.test(cleaned)) return null;
  const num = parseInt(cleaned, 16);
  const r = ((num >> 16) & 255) / 255;
  const g = ((num >> 8) & 255) / 255;
  const b = (num & 255) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  let h = 0;
  let s = 0;
  const d = max - min;
  if (d !== 0) {
    s = d / (1 - Math.abs(2 * l - 1));
    if (max === r) h = ((g - b) / d) % 6;
    else if (max === g) h = (b - r) / d + 2;
    else h = (r - g) / d + 4;
    h *= 60;
    if (h < 0) h += 360;
  }
  return `${Math.round(h)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

/**
 * Apply (or clear back to the default token value) the allow-listed Layer-2
 * override on :root. P10 §1: "Org overlay may override an allow-listed
 * subset (`--primary`, logo, portal accent) — never structural tokens" — this
 * function is the entire allow-list. Idempotent — safe to call on every boot
 * and every capabilities refetch.
 *
 * Story 6-9 extends the allow-list by three roles P10 §1 either named
 * outright or this story's own scope adds: `--accent`/`--accent-foreground`
 * (the "portal accent" role the original table already named — see
 * `contracts/orgBranding.js`'s header), the favicon (`#app-favicon`'s own
 * `href`, plain DOM, not a CSS var), and `--font-sans`/`--font-display`
 * (typography — decorative/brand, not structural, so it fits the "never
 * structural tokens" carve-out the same way color already does). Still
 * exactly what the doc comment above calls "the entire allow-list": every
 * property this function touches is enumerated here and nowhere else.
 */
export function applyBrandingOverlay(overlay: BrandingOverlay | null | undefined): void {
  const root = document.documentElement;
  const primaryTriple = overlay?.primaryColor ? hexToHslTriple(overlay.primaryColor) : null;
  const foregroundTriple = overlay?.primaryForeground ? hexToHslTriple(overlay.primaryForeground) : null;
  if (primaryTriple) root.style.setProperty('--primary', primaryTriple);
  else root.style.removeProperty('--primary');
  if (foregroundTriple) root.style.setProperty('--primary-foreground', foregroundTriple);
  else root.style.removeProperty('--primary-foreground');

  const accentTriple = overlay?.accentColor ? hexToHslTriple(overlay.accentColor) : null;
  const accentForegroundTriple = overlay?.accentForeground ? hexToHslTriple(overlay.accentForeground) : null;
  if (accentTriple) root.style.setProperty('--accent', accentTriple);
  else root.style.removeProperty('--accent');
  if (accentForegroundTriple) root.style.setProperty('--accent-foreground', accentForegroundTriple);
  else root.style.removeProperty('--accent-foreground');

  const fontStack = overlay?.font ? BRAND_FONT_STACKS[overlay.font] : null;
  if (fontStack) {
    root.style.setProperty('--font-sans', fontStack);
    root.style.setProperty('--font-display', fontStack);
  } else {
    root.style.removeProperty('--font-sans');
    root.style.removeProperty('--font-display');
  }

  const favicon = document.getElementById('app-favicon');
  if (favicon) favicon.setAttribute('href', overlay?.faviconUrl || DEFAULT_FAVICON_HREF);
}

/**
 * Persist the last-applied overlay so the NEXT page load can apply it
 * synchronously, before the capabilities fetch resolves. Read by `main.tsx`
 * at module-eval time (before `createRoot(...).render(...)`), never by a
 * React effect — the whole point is to run before React's first render.
 */
export function cacheBrandingOverlay(overlay: BrandingOverlay): void {
  try {
    // Story 6-9: was `overlay.primaryColor` alone — an org that set a logo
    // and/or ONLY a new field (accent/favicon/font, no primary color
    // override) would never get cached, so its second-and-later boots would
    // flash unbranded before the fetch resolved, the exact class of gap this
    // cache exists to close. "Any allow-listed field set" is the real
    // condition; primaryColor was just the only field checked before this
    // story widened the allow-list (a pre-existing gap for logo-only orgs
    // too, fixed here alongside the new fields rather than left standing).
    const hasOverride = Boolean(
      overlay.primaryColor || overlay.accentColor || overlay.faviconUrl || overlay.font || overlay.logoUrl
    );
    if (hasOverride) localStorage.setItem(STORAGE_KEY, JSON.stringify(overlay));
    else localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Storage unavailable (private mode / quota) — boot just starts
    // unbranded for one paint; never worth crashing over.
  }
}

export function readCachedBrandingOverlay(): BrandingOverlay {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return EMPTY_BRANDING_OVERLAY;
    const parsed = JSON.parse(raw) as Partial<BrandingOverlay>;
    return {
      logoUrl: typeof parsed.logoUrl === 'string' ? parsed.logoUrl : null,
      primaryColor: typeof parsed.primaryColor === 'string' ? parsed.primaryColor : null,
      primaryForeground: typeof parsed.primaryForeground === 'string' ? parsed.primaryForeground : null,
      accentColor: typeof parsed.accentColor === 'string' ? parsed.accentColor : null,
      accentForeground: typeof parsed.accentForeground === 'string' ? parsed.accentForeground : null,
      faviconUrl: typeof parsed.faviconUrl === 'string' ? parsed.faviconUrl : null,
      font: isBrandFont(parsed.font) ? parsed.font : null,
    };
  } catch {
    return EMPTY_BRANDING_OVERLAY;
  }
}
